import React from "react";
import { Button, RadioList } from "@bikdotai/bik-component-library";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  updateTypeFilters: readonly string[];
  selectedUpdateTypeFilter: string;
  onUpdateTypeChange: (value: string) => void;
  dateFilters: readonly string[];
  selectedDateFilter: string;
  onDateFilterChange: (value: string) => void;
  statusFilters: readonly string[];
  selectedStatusFilter: string;
  onStatusFilterChange: (value: string) => void;
  showDatePicker: boolean;
  onShowDatePicker: (show: boolean) => void;
  customStartDate: string;
  customEndDate: string;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  onClearAllFilters: () => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  updateTypeFilters,
  selectedUpdateTypeFilter,
  onUpdateTypeChange,
  dateFilters,
  selectedDateFilter,
  onDateFilterChange,
  statusFilters,
  selectedStatusFilter,
  onStatusFilterChange,
  showDatePicker,
  onShowDatePicker,
  customStartDate,
  customEndDate,
  onCustomDateChange,
  onClearAllFilters,
}) => {
  if (!isOpen) return null;

  const handleDateFilterChange = (value: string) => {
    onDateFilterChange(value);
    onShowDatePicker(value === "Custom range");
  };

  const handleClearAll = () => {
    onClearAllFilters();
    onShowDatePicker(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        style={{
          width: "400px",
          height: "100vh",
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Filter Header */}
        <div
          style={{
            padding: "24px 24px 16px 24px",
            borderBottom: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
              Filter
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Filter Content - Scrollable */}
        <div
          style={{
            flex: 1,
            padding: "24px",
            overflowY: "auto",
          }}
        >
          {/* Feature Type Filter */}
          <div style={{ marginBottom: "32px" }}>
            <h4
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#731DCF",
                marginBottom: "16px",
              }}
            >
              Feature type
            </h4>
            <RadioList
              listItems={updateTypeFilters.map((type) => ({
                isActive: selectedUpdateTypeFilter === type,
                label: type,
                value: type,
                labelSize: "MEDIUM" as const,
              }))}
              onChange={(option) => onUpdateTypeChange(option.value)}
            />
          </div>

          {/* Date Created Filter */}
          <div style={{ marginBottom: "32px" }}>
            <h4
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#731DCF",
                marginBottom: "16px",
              }}
            >
              Date created
            </h4>
            <RadioList
              listItems={dateFilters.map((date) => ({
                isActive: selectedDateFilter === date,
                label: date,
                value: date,
                labelSize: "MEDIUM" as const,
              }))}
              onChange={(option) => handleDateFilterChange(option.value)}
            />

            {/* Custom Date Range Picker */}
            {showDatePicker && selectedDateFilter === "Custom range" && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) =>
                      onCustomDateChange(e.target.value, customEndDate)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) =>
                      onCustomDateChange(customStartDate, e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div style={{ marginBottom: "32px" }}>
            <h4
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#731DCF",
                marginBottom: "16px",
              }}
            >
              Status
            </h4>
            <RadioList
              listItems={statusFilters.map((status) => ({
                isActive: selectedStatusFilter === status,
                label: status,
                value: status,
                labelSize: "MEDIUM" as const,
              }))}
              onChange={(option) => onStatusFilterChange(option.value)}
            />
          </div>
        </div>

        {/* Filter Actions - Fixed at bottom */}
        <div
          style={{
            padding: "16px 24px 24px 24px",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            gap: "12px",
            flexShrink: 0,
            alignItems: "stretch",
          }}
        >
          <div style={{ flex: 1 }}>
            <Button 
              buttonText="Apply" 
              onClick={onClose}
              matchParentWidth
              size="medium"
            />
          </div>
          <button
            onClick={handleClearAll}
            style={{
              flex: 1,
              padding: "10px 16px",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#666",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};
